import { createContext, useCallback, useState } from "react";
import { useNavigate } from "react-router";

import { apiFetch } from "./utils/api-fetch";

import type { ActiveSession } from "./session";
import type { JSX, ReactNode } from "react";

type ApiContext = {
  readonly apiFetch: typeof fetch;
  readonly isLoading: boolean;
};

type ApiContextProps = {
  readonly children: ReactNode;
  readonly session: ActiveSession;
};

export const ApiContext = createContext<ApiContext>({
  apiFetch: () => Promise.reject("api fetch is not initialized"),
  isLoading: false,
});

const isSessionExpired = async (response: Response): Promise<boolean> => {
  try {
    if (response.status === 401) {
      const payload = await response.json();
      if ("code" in payload && payload.code === "UNAUTHORIZED") {
        return true;
      }
    }
    return false;
  } catch {
    return false;
  }
};

export const ApiProvider = ({
  children,
  session,
}: ApiContextProps): JSX.Element => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const memorizedApiFetch = useCallback<typeof fetch>(
    async (url, options) => {
      setIsLoading(true);
      try {
        const response = await apiFetch(session.sessionToken)(url, options);

        if (await isSessionExpired(response)) {
          navigate("/logout");
        }

        return response;
      } finally {
        setIsLoading(false);
      }
    },
    [session.sessionToken, navigate],
  );

  return (
    <ApiContext.Provider
      value={{
        apiFetch: memorizedApiFetch,
        isLoading: isLoading,
      }}
    >
      {children}
    </ApiContext.Provider>
  );
};
